import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import { Button } from "../../design/button";
import ArtistAvatar from "../../components/ArtistAvatar";
import { priceString } from "../../utils/string";
import moment from "moment";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  PaintBrushIcon,
  ScaleIcon,
  BuildingLibraryIcon,
  LinkIcon,
} from "@heroicons/react/20/solid";
import { Artist } from "../../types";
import ReportIssueDialog from "../AuctionRecords/components/ReportIssueDialog";

interface AuctionRecordData {
  _id: string;
  title: string;
  artist: Artist;
  medium?: string;
  category?: string;
  size?: string;
  width_cm?: number;
  height_cm?: number;
  depth_cm?: number;
  usd_hammer_price?: number;
  usd_estimate_low?: number;
  usd_estimate_high?: number;
  hammer_currency?: string;
  estimate_currency?: string;
  sale_date?: string;
  auction?: {
    auction_house: { name: string; };
    title?: string;
  };
  image_url?: string;
  medium_image_url?: string;
  large_image_url?: string;
  price_per_cm_squared?: number;
  provenance?: string;
  exhibited?: string;
  literature?: string;
  is_signed?: boolean;
  is_edition?: boolean;
  edition?: string;
  url?: string;
}

const AuctionRecordDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<AuctionRecordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecord = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/auction_records/${id}`
      );
      setRecord(response.data.data);
    } catch (err) {
      setError("Failed to load auction record");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecord();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <Text className="text-red-600">{error || "Auction record not found"}</Text>
        <Button onClick={() => navigate("/auctionRecords")}>
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Search
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 bg-gray-50 dark:bg-zinc-950">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Button plain onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left Column - Image */}
          <div>
            <div className="p-4 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              {record.large_image_url || record.medium_image_url || record.image_url ? (
                <img
                  src={record.large_image_url || record.medium_image_url || record.image_url}
                  alt={record.title}
                  className="w-full h-auto rounded-lg"
                />
              ) : (
                <div className="flex items-center justify-center bg-gray-100 rounded-lg h-96 dark:bg-zinc-800">
                  <PaintBrushIcon className="w-16 h-16 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Title & Artist */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <Heading className="mb-4">{record.title}</Heading>

              <div className="flex items-center gap-3 mb-4">
                <ArtistAvatar artist={record.artist} size="md" />
                <div>
                  <Text className="font-semibold">{record.artist.name}</Text>
                  <Text className="text-sm text-gray-500">Artist</Text>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {record.category && <Badge color="blue">{record.category}</Badge>}
                {record.is_signed && <Badge color="green">Signed</Badge>}
                {record.is_edition && <Badge color="purple">Edition</Badge>}
              </div>
            </div>

            {/* Price Information */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <Subheading className="mb-4">Price Information</Subheading>

              {record.usd_hammer_price && (
                <div className="p-4 mb-4 rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                  <div className="flex items-center gap-2 mb-1">
                    <CurrencyDollarIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <Text className="text-sm text-gray-600 dark:text-gray-400">Hammer Price</Text>
                  </div>
                  <Text className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {priceString(record.usd_hammer_price / 100)}
                  </Text>
                </div>
              )}

              {record.usd_estimate_low && record.usd_estimate_high && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <Text className="text-sm text-gray-500">Estimate Low</Text>
                    <Text className="text-lg font-semibold">
                      {priceString(record.usd_estimate_low / 100)}
                    </Text>
                  </div>
                  <div>
                    <Text className="text-sm text-gray-500">Estimate High</Text>
                    <Text className="text-lg font-semibold">
                      {priceString(record.usd_estimate_high / 100)}
                    </Text>
                  </div>
                </div>
              )}

              {record.price_per_cm_squared && (
                <div>
                  <Text className="text-sm text-gray-500">Price per cm²</Text>
                  <Text className="text-lg font-semibold">
                    ${(record.price_per_cm_squared / 100).toFixed(2)}
                  </Text>
                </div>
              )}
            </div>

            {/* Physical Details */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <Subheading className="mb-4">Physical Details</Subheading>
              <div className="space-y-3">
                {record.medium && (
                  <div className="flex items-start gap-2">
                    <PaintBrushIcon className="w-5 h-5 mt-0.5 text-gray-400" />
                    <div>
                      <Text className="text-sm text-gray-500">Medium</Text>
                      <Text>{record.medium}</Text>
                    </div>
                  </div>
                )}

                {(record.width_cm || record.height_cm || record.size) && (
                  <div className="flex items-start gap-2">
                    <ScaleIcon className="w-5 h-5 mt-0.5 text-gray-400" />
                    <div>
                      <Text className="text-sm text-gray-500">Dimensions</Text>
                      {record.size ? (
                        <Text>{record.size}</Text>
                      ) : (
                        <Text>
                          {record.width_cm && `W: ${record.width_cm}cm`}
                          {record.height_cm && ` H: ${record.height_cm}cm`}
                          {record.depth_cm && ` D: ${record.depth_cm}cm`}
                        </Text>
                      )}
                    </div>
                  </div>
                )}

                {record.edition && (
                  <div>
                    <Text className="text-sm text-gray-500">Edition</Text>
                    <Text>{record.edition}</Text>
                  </div>
                )}
              </div>
            </div>

            {/* Sale Information */}
            <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
              <Subheading className="mb-4">Sale Information</Subheading>
              <div className="space-y-3">
                {record.sale_date && (
                  <div className="flex items-start gap-2">
                    <CalendarIcon className="w-5 h-5 mt-0.5 text-gray-400" />
                    <div>
                      <Text className="text-sm text-gray-500">Sale Date</Text>
                      <Text>{moment(record.sale_date).format("MMMM DD, YYYY")}</Text>
                    </div>
                  </div>
                )}

                {record.auction?.auction_house?.name && (
                  <div className="flex items-start gap-2">
                    <BuildingLibraryIcon className="w-5 h-5 mt-0.5 text-gray-400" />
                    <div>
                      <Text className="text-sm text-gray-500">Auction House</Text>
                      <Text>{record.auction.auction_house.name}</Text>
                    </div>
                  </div>
                )}

                {record.auction?.title && (
                  <div>
                    <Text className="text-sm text-gray-500">Auction Title</Text>
                    <Text>{record.auction.title}</Text>
                  </div>
                )}

                {record.url && (
                  <div>
                    <Button href={record.url} target="_blank" outline>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      View on Auction House Site
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Provenance & History */}
            {(record.provenance || record.exhibited || record.literature) && (
              <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-zinc-900">
                <Subheading className="mb-4">Provenance & History</Subheading>
                <div className="space-y-4">
                  {record.provenance && (
                    <div>
                      <Text className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Provenance
                      </Text>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {record.provenance}
                      </Text>
                    </div>
                  )}
                  {record.exhibited && (
                    <div>
                      <Text className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Exhibited
                      </Text>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {record.exhibited}
                      </Text>
                    </div>
                  )}
                  {record.literature && (
                    <div>
                      <Text className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Literature
                      </Text>
                      <Text className="text-sm text-gray-600 dark:text-gray-400">
                        {record.literature}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mt-6">
          <Button href={`/artist/${record.artist.id}`} color="blue">
            View Artist Profile
          </Button>
          <Button href={`/auctionRecords?artist=${record.artist.id}`} outline>
            More from this Artist
          </Button>
          <ReportIssueDialog item={record} onFixComplete={fetchRecord} />
        </div>
      </div>
    </div>
  );
};

export default AuctionRecordDetailPage;
